import moment from "moment"
import { fromJS } from "immutable"
import { createSelector, createStructuredSelector } from "reselect"

// selectors
import {
    selectShouldDisplayRecordDonationForm,
    selectShouldShowFeatureFlagButton,
    selectShouldShowRegionSelector,
    isUserInEURegion,
} from "shared/userdata/userdata-selectors"
import { selectFirstLedgerSettings, selectHasMultipleLedgerSettings } from "shared/pac/selectors"

// constants
import urls, { getPacPaths, searchBlacklist, searchWhitelist } from "app/static/frontend/imports/desktopPaths"

// helpers
import { checkHasPermission, checkPermission, permissionFilter } from "shared/imports/permissionFunctions"
import {
    msaSidebarItemFilter,
    ymcaSidebarItemFilter,
    kseSidebarItemFilter,
    lcSidebarItemFilter,
} from "shared/search/helperFunctions"

const selectMain = (state) => state.main

const selectQuorumAlert = createSelector(selectMain, (main) => main.get("quorumAlert", fromJS({})))

export const selectCurrentLocation = () => window.location.pathname

export const selectVerificationTime = createSelector(selectMain, (state) => state.get("login_time"))

export const lastAuthTime = createSelector(
    selectVerificationTime,
    // get the Unix ms timestamp of the datetime
    (verificationTime) => (verificationTime ? parseInt(moment(verificationTime).format("x")) : 0),
)

export const selectPreviousPage = createSelector(selectMain, (state) => {
    const prevPage = state.get("prevPages").first()

    if (prevPage) {
        return prevPage.prevPageUrl || prevPage.toJS().prevPageUrl
    }

    return undefined
})

export const securityWrapperConnection = createStructuredSelector({
    verificationTime: selectVerificationTime,
    lastAuthTime,
})

export const selectIsInSearchWhitelist = createSelector(
    (_, props) => props.location,
    (location) => searchWhitelist.some((url) => location.pathname.includes(url)),
)

export const selectIsInSearchBlacklist = createSelector(
    (_, props) => props.location,
    (_, props) => props.params,
    (location, params) =>
        searchBlacklist.some((url) => {
            const replacedUrl =
                params && Object.keys(params).length !== 0
                    ? Object.entries(params).reduce((str, [key, value]) => str.replace(`:${key}`, value), url)
                    : url

            return location.pathname.includes(replacedUrl)
        }),
)

export const shouldDisplayRecordDonationForm = createSelector(
    isUserInEURegion,
    selectShouldDisplayRecordDonationForm,
    (isInEu, displayRecordDonationForm) => !isInEu && displayRecordDonationForm,
)

export const selectAddDisbursementLink = createSelector(
    selectFirstLedgerSettings,
    selectHasMultipleLedgerSettings,
    (ledgerSettings, hasMultipleLedgerSettings) => {
        if (checkHasPermission([DjangIO.app.models.PermissionType.qpac_v1]) && ledgerSettings) {
            return hasMultipleLedgerSettings
                ? urls.pacList // If organization has multiple PACs, redirect them to PAC list page
                : getPacPaths({ ledgerSettingsId: ledgerSettings.get("id") })._pacDisbursementNew // If organization has singular PAC, redirect to Create Disbursement page
        }

        return ""
    },
)

export const selectAddReceiptLink = createSelector(
    selectFirstLedgerSettings,
    selectHasMultipleLedgerSettings,
    (ledgerSettings, hasMultipleLedgerSettings) => {
        if (checkHasPermission([DjangIO.app.models.PermissionType.qpac_v1]) && ledgerSettings) {
            return hasMultipleLedgerSettings
                ? urls.pacList // If organization has multiple PACs, redirect them to PAC list page
                : getPacPaths({ ledgerSettingsId: ledgerSettings.get("id") })._pacReceiptNew // If organization has singular PAC, redirect to Create Receipt page
        }

        return ""
    },
)

export const selectSideBarItems = createSelector(
    selectMain, // Include state selector so selector is only called once
    () =>
        DjangIO.app.models.SidebarItem.region_supported_items()
            .filter(kseSidebarItemFilter)
            .filter(msaSidebarItemFilter)
            .filter(ymcaSidebarItemFilter)
            .filter(lcSidebarItemFilter)
            .filter(permissionFilter)
            .filter((item) => item.value !== DjangIO.app.models.SidebarItem.files.value)
            .filter((item) => Userdata.sidebar_settings_list.includes(item.value))
            .filter((item) => {
                // CUSTOM FILTERING GOES HERE
                // This type of filtering might occur because we have a user-specific
                // permission that might override the org permission, and as of yet
                // we don't have a "all permissions required" enum field
                if (
                    item.value === DjangIO.app.models.SidebarItem.campaigns.value &&
                    !checkPermission(DjangIO.app.models.PermissionType.can_access_grassroots)
                ) {
                    return false
                }

                return true
            })
            .sort((a, b) => {
                const aOrder = Userdata.sidebar_settings_list.indexOf(a.value)
                const bOrder = Userdata.sidebar_settings_list.indexOf(b.value)
                return aOrder - bOrder
            }),
)

export const selectSideBarMoreOptionsItems = createSelector(
    selectMain, // Include state selector so selector is only called once
    () =>
        DjangIO.app.models.SidebarItem.region_supported_items()
            .filter(kseSidebarItemFilter)
            .filter(msaSidebarItemFilter)
            .filter(ymcaSidebarItemFilter)
            .filter(lcSidebarItemFilter)
            .filter((item) => item.value !== DjangIO.app.models.SidebarItem.files.value)
            .filter((item) =>
                permissionFilter(item, {
                    regions: item.supported_regions,
                    excludedRegions: item.excluded_by_regions,
                    useCurrentRegions: true,
                }),
            )
            .filter((item) => !Userdata.sidebar_settings_list.includes(item.value))
            // filter out sheets and download center if user is consultant
            .filter(
                (item) =>
                    !(
                        Userdata.limited &&
                        (item === DjangIO.app.models.SidebarItem.sheets ||
                            item === DjangIO.app.models.SidebarItem.new_downloads)
                    ),
            )
            .filter((item) => {
                // Edge cases:
                // 1. If organization is an i360 org, remove 'Action Center' as a side bar item.
                //  Unless the organization is also a Quorum client and has stakeholder, then do not remove 'Action Center'
                if (
                    item === DjangIO.app.models.SidebarItem.campaigns &&
                    Userdata.i360_organization &&
                    !Userdata.i360_client_with_stakeholder
                ) {
                    return false
                }

                // 2. If they do not have permission to access grassroots, remove 'Action Center' option
                if (
                    item.value === DjangIO.app.models.SidebarItem.campaigns.value &&
                    !checkPermission(DjangIO.app.models.PermissionType.can_access_grassroots)
                ) {
                    return false
                }

                return true
            })
            .sort((a, b) => a.value - b.value),
)

export const selectQuorumAlertConfig = createSelector(selectQuorumAlert, (alert) =>
    // TODO: Is there actually a performance implication here?
    alert.get("config", fromJS({})).toJS(),
)

export const selectQuorumAlertYieldResult = createSelector(selectQuorumAlert, (alert) => alert.get("yieldResult"))

export const selectQuorumAlertYieldResultAndClose = createSelector(selectQuorumAlert, (alert) =>
    alert.get("yieldResultAndClose"),
)

export const selectQuorumAlertIsOpen = createSelector(selectQuorumAlert, (alert) => alert.get("isOpen"))

export const frameConnection = createStructuredSelector({
    addDisbursementLink: selectAddDisbursementLink,
    addReceiptLink: selectAddReceiptLink,
    isInEU: isUserInEURegion,
    isInSearchBlacklist: selectIsInSearchBlacklist,
    isInSearchWhitelist: selectIsInSearchWhitelist,
    shouldDisplayRecordDonationForm,
    shouldShowFeatureFlagButton: selectShouldShowFeatureFlagButton,
    shouldShowRegionSelector: selectShouldShowRegionSelector,
    sideBarItems: selectSideBarItems,
    sideBarMoreOptionsItems: selectSideBarMoreOptionsItems,
})

export const quorumAlertConnection = createStructuredSelector({
    config: selectQuorumAlertConfig,
    isOpen: selectQuorumAlertIsOpen,
    yieldResult: selectQuorumAlertYieldResult,
    yieldResultAndClose: selectQuorumAlertYieldResultAndClose,
})
